import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getUserAddress, placeOrder } from '../services/api';
import { formatPrice } from '../utils/formatPrice';
import toast from 'react-hot-toast';
const steps = ['Delivery Address', 'Payment Method', 'Confirm Order'];

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { isAuthenticated, user } = useAuth();

  const { discountAmount = 0 } = location.state || {}; // receive discount from Cart
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [address, setAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cod');

  const subtotal = getCartTotal();
  const deliveryCharge = subtotal >= 499 ? 0 : 40;
  const total = Math.max(subtotal + deliveryCharge - discountAmount, 0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }

    const fetchAddress = async () => {
      try {
        const userId = localStorage.getItem('UserId');
        const response = await getUserAddress(userId);
        if (response.data.data && response.data.data[0]) {
          const addr = response.data.data[0];
          setAddress({
            fullAddress: `${addr.Address}, ${addr.City}, ${addr.State}`,
            ...addr,
          });
        }
      } catch (error) {
        console.error('Error fetching address:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddress();
  }, [isAuthenticated, cartItems.length, navigate]);

  const handlePlaceOrder = async () => {
    setPlacing(true);
    try {
      // Backend ko ek object bhejo, sirf array nahi
      await placeOrder({
        items: cartItems, 
        couponCode: localStorage.getItem("appliedCouponCode") || null,
        discountAmount: discountAmount,
        userId: user?._id,
        address: address
      });
  
      // Success hone par safai
      localStorage.removeItem("couponApplied");
      localStorage.removeItem("appliedCouponCode");
      localStorage.removeItem("couponDiscount");
      localStorage.removeItem("couponPercent");
      clearCart();
      
      toast.success('Order placed!');
      navigate('/orders');
    } catch (err) {
      toast.error('Failed!');
    } finally {
      setPlacing(false);
    }
  };
  if (loading) return <Loader message="Loading checkout..." />;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb
          items={[
            { label: 'Cart', path: '/cart' },
            { label: 'Checkout' },
          ]}
        />

        <Typography variant="h4" fontWeight="bold" color="#3E2723" sx={{ mb: 3 }}>
          Checkout
        </Typography>

        <Stepper activeStep={0} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Grid container spacing={4}>
          {/* Left Side - Forms */}
          <Grid item xs={12} md={8}>
            {/* Delivery Address */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocalShippingIcon sx={{ color: '##876513' }} />
                <Typography variant="h6" fontWeight="bold">
                  Delivery Address
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {address ? (
                <Box
                  sx={{
                    p: 2,
                    border: '2px solid ##876513',
                    borderRadius: 2,
                    bgcolor: '#faf8f5',
                  }}
                >
                  <Typography fontWeight="bold">{user?.name}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    {address.fullAddress}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    No address found. Please add your delivery address.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Address"
                        placeholder="House No, Street, Area"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="City" size="small" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="State" size="small" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Pin Code" size="small" />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>

            {/* Payment Method */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PaymentIcon sx={{ color: '##876513' }} />
                <Typography variant="h6" fontWeight="bold">
                  Payment Method
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel
                  value="cod"
                  control={<Radio sx={{ color: '##876513', '&.Mui-checked': { color: '##876513' } }} />}
                  label={
                    <Box>
                      <Typography fontWeight="bold">Cash on Delivery</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay when you receive your order
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: paymentMethod === 'cod' ? '2px solid ##876513' : '1px solid #ddd',
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                    bgcolor: paymentMethod === 'cod' ? '#faf8f5' : 'transparent',
                  }}
                />
                <FormControlLabel
                  value="upi"
                  control={<Radio sx={{ color: '##876513', '&.Mui-checked': { color: '##876513' } }} />}
                  label={
                    <Box>
                      <Typography fontWeight="bold">UPI Payment</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay using Google Pay, PhonePe, Paytm
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: paymentMethod === 'upi' ? '2px solid ##876513' : '1px solid #ddd',
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                    bgcolor: paymentMethod === 'upi' ? '#faf8f5' : 'transparent',
                  }}
                />
                <FormControlLabel
                  value="card"
                  control={<Radio sx={{ color: '##876513', '&.Mui-checked': { color: '##876513' } }} />}
                  label={
                    <Box>
                      <Typography fontWeight="bold">Credit/Debit Card</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visa, Mastercard, RuPay
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: paymentMethod === 'card' ? '2px solid ##876513' : '1px solid #ddd',
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: paymentMethod === 'card' ? '#faf8f5' : 'transparent',
                  }}
                />
              </RadioGroup>
            </Paper>
          </Grid>

          {/* Right Side - Order Summary */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#fff',
                position: 'sticky',
                top: 100,
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Order Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Cart Items Preview */}
              <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                {cartItems.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      pb: 2,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <img
                      src={item.ProductMainImgUrl}
                      alt={item.ProductName}
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.ProductName.slice(0, 30)}...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Qty: {item.ProductQuantity}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      Rs.{formatPrice(item.ProductPrice * item.ProductQuantity)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Subtotal */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography fontWeight="bold">Rs.{formatPrice(subtotal)}</Typography>
              </Box>

              {/* Delivery */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Delivery</Typography>
                <Typography fontWeight="bold" color={deliveryCharge === 0 ? '#2e7d32' : 'inherit'}>
                  {deliveryCharge === 0 ? 'FREE' : `Rs.${deliveryCharge}`}
                </Typography>
              </Box>

              {/* Discount */}
              {discountAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">Discount</Typography>
                  <Typography fontWeight="bold" color="#2e7d32">
                    -Rs.{formatPrice(discountAmount)}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Total */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                  Rs.{formatPrice(total)}
                </Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handlePlaceOrder}
                disabled={placing || !address}
                startIcon={<CheckCircleIcon />}
                sx={{
                  py: 1.5,
                  bgcolor: '#2e7d32',
                  '&:hover': { bgcolor: '#1b5e20' },
                  fontWeight: 'bold',
                  fontSize: '1rem',
                }}
              >
                {placing ? 'Placing Order...' : 'Place Order'}
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                By placing this order, you agree to our Terms & Conditions
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Footer />
    </Box>
  );
};

export default Payment;